import React, { useState, useRef } from "react";
import { Paper, Tabs, Tab, Box, Typography, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CustomTabPanel from "components/atoms/CustomTabPanel"; 
import CustomRightDrawer from "components/atoms/CustomRightDrawer"; 
import ClinicalRecordForm from "components/molecules/patient/singlePatient/medicalRecords/ClinicalRecordForm";
import { useGetClinicalRecords } from "store/hooks/react-query/query/useClinicalRecords";
import CustomLoader from "components/atoms/CustomLoader";

function RecordsTable({ patientId, type }) {
  const { data, isLoading } = useGetClinicalRecords(patientId, type);

  if (isLoading) return <CustomLoader />;
  if (!data?.data?.length) return <Typography p={2} color="textSecondary">No records found.</Typography>;

  // Special 8-Column Render for Vital Signs
  if (type === 'VITAL_SIGNS') {
    return (
      <TableContainer component={Box} sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>BP</TableCell>
              <TableCell>Temp</TableCell>
              <TableCell>Pulse</TableCell>
              <TableCell>Resp</TableCell>
              <TableCell>SPO2</TableCell>
              <TableCell>RBS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.data.map((row) => {
              const d = new Date(row.date);
              return (
                <TableRow key={row._id}>
                  <TableCell>{d.toLocaleDateString()}</TableCell>
                  <TableCell>{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
                  <TableCell>{row.details.bp || "-"}</TableCell>
                  <TableCell>{row.details.temp ? `${row.details.temp} °C` : "-"}</TableCell>
                  <TableCell>{row.details.pulse || "-"}</TableCell>
                  <TableCell>{row.details.resp || "-"}</TableCell>
                  <TableCell>{row.details.spo2 ? `${row.details.spo2}%` : "-"}</TableCell>
                  <TableCell>{row.details.rbs || "-"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Standard Render for everything else
  return (
    <TableContainer component={Box} sx={{ maxHeight: 400, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Details</TableCell>
            {type === 'FLUID_BALANCE' && <TableCell align="right">Balance</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.data.map((row) => (
            <TableRow key={row._id}>
              <TableCell>{new Date(row.date).toLocaleString()}</TableCell>
              
              <TableCell>
                {type === 'FLUID_BALANCE' && (
                  <Box>
                    <Typography variant="caption" display="block">Input: {row.details.inputType} ({row.details.inputVolume}ml)</Typography>
                    <Typography variant="caption" display="block">Output: {row.details.outputType} ({row.details.outputVolume}ml)</Typography>
                  </Box>
                )}
                {type === 'CLINICAL_NOTE' && (
                  <Box>
                    <Chip label={row.details.role} size="small" color={row.details.role === 'DOCTOR' ? 'primary' : 'secondary'} sx={{mr: 1}}/>
                    <Typography component="span" fontWeight="bold">{row.details.authorName}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>{row.details.note}</Typography>
                  </Box>
                )}
                {type === 'MEDICATION' && (
                   <Box>
                     {/* Safe fallback map for the new array structure */}
                     {row.details.medications?.map((m, i) => (
                       <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                         • <strong>{m.medicationName}</strong> 
                         {m.dose && ` - ${m.dose}`} 
                         {m.frequency && ` (${m.frequency})`}
                       </Typography>
                     ))}
                   </Box>
                )}
              </TableCell>

              {type === 'FLUID_BALANCE' && (
                <TableCell align="right">
                  <Chip label={`${row.details.balance} ml`} color={row.details.balance < 0 ? "error" : "success"} size="small" />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function ClinicalDocumentation({ patientId }) {
  const [tabValue, setTabValue] = useState(0);
  const drawerRef = useRef();

  const toggleDrawer = () => { if(drawerRef.current) drawerRef.current.handleToggle(); }

  return (
    <Paper sx={{ p: 2, minHeight: "50vh" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">Clinical Documentation</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={toggleDrawer}>
          Add Entry
        </Button>
      </Stack>

      {/* Added Vital Signs Tab */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} indicatorColor="secondary" variant="scrollable">
        <Tab label="Vital Signs" />
        <Tab label="Fluid Balance" />
        <Tab label="Clinical Notes" />
        <Tab label="Medication" />
      </Tabs>

      <CustomTabPanel value={tabValue} index={0}>
        <RecordsTable patientId={patientId} type="VITAL_SIGNS" />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        <RecordsTable patientId={patientId} type="FLUID_BALANCE" />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={2}>
        <RecordsTable patientId={patientId} type="CLINICAL_NOTE" />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={3}>
        <RecordsTable patientId={patientId} type="MEDICATION" />
      </CustomTabPanel>

      <CustomRightDrawer ref={drawerRef} title="New Clinical Entry">
        <ClinicalRecordForm patientId={patientId} onSuccess={toggleDrawer} />
      </CustomRightDrawer>
    </Paper>
  );
}